import type {
	AnimationsBehavior,
	BaseDataItem,
	LayoutDataItems,
	MasterPageDefinition,
	Style,
	ThemeFontMap,
	TransformationDataItem,
	Component,
} from '@wix/thunderbolt-becky-types'
import type {
	CompNode,
	QueriesRefs,
	RefArrayViewItem,
	Unref,
	VariantRelationViewItem } from '@wix/thunderbolt-catharsis'
import {
	createCompNode,
	envRefs,
	isRefArrayViewItem,
	qaRefs,
	themeRefs
} from '@wix/thunderbolt-catharsis'
import type { CompInfo, PageTransition } from '@wix/thunderbolt-components-native'
import { refApi as thunderboltRefsAPI } from '@wix/thunderbolt-refs'
import {
	color as commonColor,
	getDirection,
	getVariableCss,
	resolveSpxAttribute,
} from '@wix/thunderbolt-commons'
import {
	fontObjectToString,
	getCompDefaultLayout,
	getClassicLayout,
	getClassicTransformation,
	parseFontStr,
} from '@wix/thunderbolt-becky-root'
import { previewCompInfoNodes } from './previewCompInfoResolvers'
import type { BreakpointsVariantsTo } from './tempVariantUtils'
import { DESKTOP_SPX, MOBILE_SPX, getVariant } from './tempVariantUtils'
import { assign } from 'lodash'
import { getColorToWixThemeVariableMap, getWixColorToWixThemeVariableMap } from '@wix/wix-site-theme-variables'

const { getSplitRgbValuesString } = commonColor

const MOBILE_SITE_WIDTH: number = 320
const DEFAULT_DESKTOP_SITE_WIDTH: number = 980

export const isRefArray = <T extends BaseDataItem>(item?: BaseDataItem): item is RefArrayViewItem<T> =>
	item?.type === 'RefArray'

const compId = createCompNode({
	getDependencies: () => { },
	toViewItem: (component) => component.id,
})

const structure = createCompNode({
	getDependencies: () => { },
	toViewItem: (component) => component,
})

const compLayout = createCompNode({
	getDependencies: () => { },
	toViewItem: (component) => component.layout,
})

const innerElements = createCompNode({
	getDependencies: () => { },
	toViewItem: (component) => component.innerElementsQuery,
})

const compSingleLayout = createCompNode({
	getDependencies: (component, refs) => ({
		compLayouts: refs.layoutQuery(component.layoutQuery!),
		isMobileView: envRefs.isMobileView,
		isOneDocMigrated: envRefs.isOneDocMigrated,
	}),
	toViewItem: (component, deps) =>
		getClassicLayout(deps.compLayouts?.values || [], deps.isMobileView, deps.isOneDocMigrated),
})

const refApi = createCompNode({
	getDependencies: () => { },
	toViewItem: () => thunderboltRefsAPI,
})

/**
 *  hasResponsiveLayout @DEPRECATED please use isClassicComponent instead
 * */
const hasResponsiveLayout = createCompNode({
	getDependencies: (component, refs) => ({
		layoutData: refs.layoutQuery(component.layoutQuery!),
		isMobileView: envRefs.isMobileView,
		isOneDocMigrated: envRefs.isOneDocMigrated,
		isResponsive: envRefs.isResponsive,
	}),
	toViewItem: (component, deps) => {
		if (component.id === 'masterPage') {
			return deps.isResponsive
		}

		if (deps.isOneDocMigrated) {
			const layouts = deps.layoutData?.values as Array<LayoutDataItems | VariantRelationViewItem<LayoutDataItems>>
			const compDefaultLayout = getCompDefaultLayout(layouts, deps.isMobileView)
			return !!layouts && compDefaultLayout?.componentLayout?.type !== 'ClassicComponentLayout'
		}

		return !!deps.layoutData
	},
})

const isClassicComponent = createCompNode({
	getDependencies: (component, refs) => ({
		layoutData: refs.layoutQuery(component.layoutQuery!),
		isMobileView: envRefs.isMobileView,
		isOneDocMigrated: envRefs.isOneDocMigrated,
		isResponsive: envRefs.isResponsive,
	}),
	toViewItem: (component, deps) => {
		if (component.id === 'masterPage') {
			return !deps.isResponsive
		}

		if (deps.isOneDocMigrated) {
			const layouts = deps.layoutData?.values
			const compDefaultLayout = getCompDefaultLayout(layouts, deps.isMobileView)
			return compDefaultLayout?.componentLayout?.type === 'ClassicComponentLayout'
		}

		return !deps.layoutData
	},
})

const classicRotation = createCompNode({
	getDependencies: (component, refs) => ({
		transformationData: refs.transformationQuery(component.transformationQuery!),
		isMobileView: envRefs.isMobileView,
		isOneDocMigrated: envRefs.isOneDocMigrated,
	}),
	toViewItem: (__, deps) => {
		const transformationItems = (deps.transformationData?.values || []) as Array<TransformationDataItem>

		if (!isOneDocMigrated || !transformationItems?.length) {
			return 0
		}

		const classicTransform = getClassicTransformation(transformationItems, deps.isMobileView, deps.isOneDocMigrated)
		return classicTransform?.rotate ?? 0
	},
})

const compStylableClass = createCompNode({
	getDependencies: () => { },
	toViewItem: (component) => `${component.styleId}__root`,
})

const customClassNames = createCompNode({
	getDependencies: (component, refs) => refs.classnamesQuery(component.classnamesQuery!),
	toViewItem: (__, data) => data?.classnames ?? [],
})

const isMasterPage = createCompNode({
	getDependencies: () => { },
	toViewItem: (component) => component.metaData.pageId === 'masterPage',
})

const compData = createCompNode({
	getDependencies: (component, refs) => refs.dataQuery(component.dataQuery!),
	toViewItem: (__, data) => data,
})

const compDesign = createCompNode({
	getDependencies: (component, refs) => refs.designQuery(component.designQuery!),
	toViewItem: (__, data) => data,
})

const compAnchor = createCompNode({
	getDependencies: (component, refs) => refs.anchorQuery(component.anchorQuery!),
	toViewItem: (__, data) => data,
})

const EMPTY_PROPS = {}
const compProps = createCompNode({
	getDependencies: (component, refs) => refs.propertyQuery(component.propertyQuery!),
	toViewItem: (__, data) => data ?? EMPTY_PROPS,
})

const EMPTY_ANIMATIONS: Array<AnimationsBehavior> = []
const animations = createCompNode({
	getDependencies: (component, refs) => refs.behaviorQuery(component.behaviorQuery!),
	toViewItem: (__, data) => data?.animations ?? EMPTY_ANIMATIONS,
})

const compBehaviors = createCompNode({
	getDependencies: (component, refs) => refs.behaviorQuery(component.behaviorQuery!),
	toViewItem: (__, data) => data?.behaviors ?? [],
})

const siteWidth = createCompNode({
	getDependencies: () => ({
		siteWidth: envRefs.siteWidth,
		viewMode: envRefs.viewMode,
	}),
	toViewItem: (__, data) => {
		if (data.viewMode === 'mobile') {
			return MOBILE_SITE_WIDTH
		}

		return data.siteWidth ?? DEFAULT_DESKTOP_SITE_WIDTH
	},
})

const skin = createCompNode({
	getDependencies: (component, refs) => (component.styleId ? refs.styleId(component.styleId) : null),
	toViewItem: (component, style) => {
		if (!style) {
			return component.skin
		}

		const defaultStyle = isRefArray(style)
			? (style.values.find((item) => 'skin' in item) as Style | undefined)
			: style

		return defaultStyle?.skin ?? component.skin
	},
})

const mainPageId = createCompNode({
	getDependencies: (__, refs) => refs.dataQuery<MasterPageDefinition['data']>('masterPage'),
	toViewItem: (__, data) => {
		return data.mainPageId || data.mainPage?.id || data.id
	},
})

const globalImageQuality = createCompNode({
	getDependencies: (__, refs) => refs.dataQuery('IMAGE_QUALITY'),
	toViewItem: (__, data) => data as CompInfo['globalImageQuality'],
})

const envCompNode = <Key extends keyof typeof envRefs>(key: Key) =>
	createCompNode<any, NonNullable<Unref<typeof envRefs[Key]>>, any>({
		getDependencies: () => envRefs[key],
		toViewItem: (__, data: any) => data,
	})

const isSocialElementsBlocked = envCompNode('isSocialElementsBlocked')

const deviceType = envCompNode('deviceType')
const experiments = envCompNode('experiments')
const externalBaseUrl = envCompNode('externalBaseUrl')
const isMobileView = envCompNode('isMobileView')
const isTrackClicksAnalyticsEnabled = envCompNode('isTrackClicksAnalyticsEnabled')
const isNonAdaptiveMobile = envCompNode('isNonAdaptiveMobile')
const isQaMode = envCompNode('qaMode')
const isInSeo = envCompNode('isInSeo')
const language = envCompNode('language')
const metaSiteId = envCompNode('metaSiteId')
const topology = envCompNode('publicTopology')
const viewMode = envCompNode('viewMode')
const editorName = envCompNode('editorName')
const isResponsive = envCompNode('isResponsive')
const formFactor = envCompNode('formFactor')
const anywhereThemeOverride = envCompNode('anywhereThemeOverride')
const isOneDocMigrated = envCompNode('isOneDocMigrated')

const siteColors = createCompNode({
	getDependencies: () => themeRefs.siteColors,
	toViewItem: (__, data) => data,
})

const siteFonts = createCompNode({
	getDependencies: () => themeRefs.siteFonts,
	toViewItem: (__, data) => {
		if (!data) {
			return {}
		}

		return data.reduce((acc, font, index) => {
			acc[`font_${index}`] = font
			return acc
		}, {} as ThemeFontMap)
	},
})

const getDefaultStyleItem = (styleItem: Style | RefArrayViewItem<Style>) => {
	if (isRefArrayViewItem<Style>(styleItem)) {
		return styleItem.values.find((item) => 'style' in item) as Style | undefined
	} else {
		return styleItem
	}
}

const styleProperties = createCompNode<
	ReturnType<QueriesRefs['styleId']>,
	CompInfo<any>['styleProperties'],
	QueriesRefs['styleId']
>({
	getDependencies: (component, refs) => refs.styleId(component.styleId!),
	toViewItem: (__, data) => {
		if (!data) {
			return {}
		}

		const defaultStyle = getDefaultStyleItem(data)
		if (defaultStyle?.spx) {
			return { ...defaultStyle.style?.properties, spx: defaultStyle.spx } as CompInfo<any>['styleProperties']
		}
		return defaultStyle?.style?.properties || {}
	},
})

const direction = createCompNode({
	getDependencies: () => envRefs.language,
	toViewItem: (__, data) => getDirection(data),
})

const directionWithOptInRtlSupport = createCompNode({
	getDependencies: () => ({
		currentSiteDirection: envRefs.currentSiteDirection,
	}),
	toViewItem: (__, data) => data.currentSiteDirection || 'ltr',
})

const isConnectedToDataBinding = createCompNode({
	getDependencies: (component, refs) => refs.connectionQuery(component.connectionQuery!),
	toViewItem: (__, data) => {
		if (!data?.controllerConnections) {
			return false
		}

		// applicationId is actually appDefinitionId, and there is need in data migration from DM to replace the naming.
		return data.controllerConnections.some((item) => item.applicationId === 'dataBinding')
	},
})

const notImplemented = createCompNode<any, any, any>({
	getDependencies: () => { },
	toViewItem: () => ({}),
})

const fullNameCompType = createCompNode({
	getDependencies: (component) => qaRefs.fullNameCompType(component.id),
	toViewItem: (__, data) => data,
})

const originalComponentType = createCompNode({
	getDependencies: (component) => qaRefs.fullNameCompType(component.id),
	toViewItem: (__, data) => {
		const splittedFullName = data.split('.')
		return splittedFullName[splittedFullName.length - 1]
	},
})

const formatCssValue = createCompNode<unknown, typeof resolveSpxAttribute, any>({
	getDependencies: () => { },
	toViewItem: () => resolveSpxAttribute,
})

const siteFontsSpxResolved = createCompNode({
	getDependencies: () => themeRefs.siteFonts,
	toViewItem: (__, fonts) =>
		fonts.reduce((acc, font, index) => {
			const fontObj = parseFontStr(font)
			const resolvedFontObj =
				fontObj.sizeUnit === 'spx'
					? { ...fontObj, size: `calc(${fontObj.size} * var(--theme-spx-ratio))`, sizeUnit: '' }
					: fontObj
			const fontString = fontObjectToString(resolvedFontObj, true)
			acc[`font_${index}`] = fontString
			return acc
		}, {} as ThemeFontMap),
})

const LEGACY_TRANSITION_TYPES_MAP: Record<string, PageTransition> = {
	outIn: 'OutIn',
	crossfade: 'CrossFade',
	shrinkfade: 'CrossFade',
	swipeHorizontal: 'SlideHorizontal',
	swipeHorizontalFullScreen: 'SlideHorizontal',
	swipeVertical: 'SlideVertical',
	swipeVerticalFullScreen: 'SlideVertical',
}

const pageTransition = createCompNode({
	getDependencies: (_component, refs) => ({
		isMobile: envRefs.isMobileView,
		sitePagesProperties: refs.propertyQuery<MasterPageDefinition['property']>('SITE_PAGES'),
		// TODO: Check if we can get the page list as a dependency
	}),
	toViewItem: (__, data): PageTransition => {
		const { isMobile, sitePagesProperties } = data
		// Should return none if we are in mobile view or we have only one page
		if (isMobile) {
			return 'none'
		}

		const transition = LEGACY_TRANSITION_TYPES_MAP[sitePagesProperties!.transition] || 'none'
		return transition as PageTransition
	},
})

const shouldShowFreemiumBanner = createCompNode({
	getDependencies: () => ({
		renderWixAdsOnNavigation: envRefs.shouldRenderWixAdsOnNavigation,
		isWixAdsAllowed: envRefs.isWixAdsAllowed,
	}),
	toViewItem: (__, data) => data.renderWixAdsOnNavigation && data.isWixAdsAllowed,
})

const isDefaultVariant = createCompNode({
	getDependencies: () => { },
	toViewItem: () => true,
})

const isBuilderComponentModel = createCompNode({
	getDependencies: () => envRefs.isBuilderComponentModel,
	toViewItem: (__, data) => data,
})

type CssVariables = { [varName: string]: string }
export type VariablesCssData = BreakpointsVariantsTo<CssVariables>

/**
 * These constants are a temporary hack!
 * In the future we will have refWidth related to varianteable theme, which will include the spx.
 * BUT- for now, and for editor 3 specifically, this is enough.
 */

const getSpxFromBreakpoint = (breakpointId: string) => {
	return breakpointId === 'default' ? DESKTOP_SPX : MOBILE_SPX
}

export const siteVariables = createCompNode({
	getDependencies: (component: Component, refs) => {
		return {
			variablesList: refs.variablesQuery('variables-masterPage'),
			environmentRefs: { renderSticky: envRefs.renderSticky },
			experiments: envRefs.experiments,
		}
	},
	toViewItem: (
		_,
		{ variablesList, environmentRefs, experiments }
	): { [key: string]: string } => {
		// Duplicated code from `variablesCompNode.ts` for resolving variables data per breakpoint
		if (!variablesList || !variablesList.variables) {
			return {}
		}
		const isNewStyleApi = !!experiments['specs.thunderbolt.newStyleApi']

		const variableMap = variablesList.variables.reduce<VariablesCssData>(
			(acc, { id: varId, value: refArray, cssVarName }) => {
				const varKey = isNewStyleApi && cssVarName ? cssVarName : varId

				refArray.values.forEach((val) => {
					const { breakpointId, variantsKey, value } = getVariant(val)
					acc[breakpointId] = acc[breakpointId] || {}
					acc[breakpointId][variantsKey] = acc[breakpointId][variantsKey] || {}
					assign(
						acc[breakpointId][variantsKey],
						getVariableCss(
							value,
							varKey,
							environmentRefs,
							isNewStyleApi,
							getSpxFromBreakpoint(breakpointId)
						)
					)
				})

				return acc
			},
			{}
		)

		const colorMap = getColorToWixThemeVariableMap()
		const wixColorMap = getWixColorToWixThemeVariableMap()

		const defaultColorsMap = variableMap.default['']

		// Create the "base" variables map, that aren't dependnt on any other variables
		// TODO: this might miss "base" items that relies on spx and is considered base?
		const baseVarsMap = Object.entries(defaultColorsMap).reduce((acc: { [key: string]: string }, [key, value]) => {
			const hasVar = value.includes('var(')

			if (hasVar) {
				return acc
			}

			acc[key] = value

			return acc
		}, {})

		// Here we create a joint map, with the base items, and resolved items that were dependent on "base" values
		const resolvedVarsMap = Object.entries(defaultColorsMap).reduce((acc: { [key: string]: string }, [key, value]) => {
			const hasVar = value.includes('var(')
			const newKey = key.replace(/^--/, '')

			if (hasVar) {
				let newValue = value
				const variableRegex = /var\(([\w-]+)\)/g
				const variableValues = variableRegex.exec(value)
				if (variableValues) {
					variableValues.forEach((varValue) => {
						if (baseVarsMap[varValue]) {
							newValue = newValue.replace(`var(${varValue})`, baseVarsMap[varValue])
						}
					})
				}

				acc[newKey] = newValue
			} else {
				acc[newKey] = value
			}

			return acc
		}, {})

		// Transform all the variables to RGB triplets
		const correctColorMapFromVars = Object.entries({ ...colorMap, ...wixColorMap }).reduce((acc: { [key: string]: string }, [key, value]) => {
			const color = resolvedVarsMap[value!] ?? value

			const rgbValues = color.includes('#') ? (getSplitRgbValuesString(color) ?? color) : color
			const newKey = `--${key}`
			acc[newKey] = rgbValues
			return acc
		}, {})

		return correctColorMapFromVars
	},
})

// Update CompInfo in editor-elements to include the following nodes and then move them to publicCompInfoNodes
const newCompInfoNodes = {
	animations,
	customClassNames,
	formFactor,
	isNonAdaptiveMobile,
	originalComponentType,
	pageTransition,
	shouldShowFreemiumBanner,
	siteWidth,
	isTrackClicksAnalyticsEnabled,
	isResponsive,
	isDefaultVariant,
	isBuilderComponentModel,
	siteVariables,
}

const publicCompInfoNodes: Required<
	{
		[key in keyof CompInfo<any>]: CompNode<any, CompInfo<any>[key], any>
	}
> = {
	compBehaviors,
	compData,
	// @ts-ignore TODO: remove once added to EE types
	isSocialElementsBlocked,
	compDesign,
	compId,
	compLayout,
	compSingleLayout,
	compAnchor,
	compProps,
	compStylableClass,
	deviceType,
	direction,
	// @ts-ignore TODO: remove once added to EE types
	directionWithOptInRtlSupport,
	editorName,
	experiments,
	externalBaseUrl,
	formatCssValue,
	fullNameCompType,
	globalImageQuality,
	hasResponsiveLayout,
	isConnectedToDataBinding,
	isInSeo,
	isMasterPage,
	isMobileView,
	isQaMode,
	language,
	mainPageId,
	metaSiteId,
	refApi,
	siteColors,
	siteFonts,
	siteFontsSpxResolved,
	skin,
	structure,
	styleProperties,
	topology,
	viewMode,
	compMeshProps: notImplemented,
	uiType: notImplemented,
	pagesMap: notImplemented,
	// @ts-ignore TODO: remove once added to EE types
	hierarchyPagesMap: notImplemented,
	isInFirstFold: notImplemented,
	translate: notImplemented,
	trackClicksAnalytics: notImplemented,
	activeVariant: notImplemented,
	compVariants: notImplemented,
	// @ts-ignore
	isDescendantOfBlocksWidget: notImplemented,
	isOneDocMigrated,
	classicLayout: compSingleLayout,
	classicRotation,
	isClassicComponent,
	// @ts-ignore TODO type
	innerElements,
}

export const compInfoNodes = {
	...publicCompInfoNodes,
	...newCompInfoNodes,
	anywhereThemeOverride,
	...previewCompInfoNodes,
}
